import React, { useEffect, useMemo, useRef, useState } from 'react';

import { Modal } from '@/components';
import { Alert } from '@/design-system/alert';
import { Badge } from '@/design-system/badge';
import { Button } from '@/design-system/button';
import { TableData, type TableDataColumn } from '@/design-system/table-data';
import { Tabs, TabsCount, TabsList, TabsTrigger } from '@/design-system/tabs';
import { toast } from '@/design-system/toaster';

import {
  type CsvImportContext,
  parseWarehouseError,
  warehouseApi,
} from '../api/warehouse.api';
import {
  csvFileError,
  downloadBlob,
  downloadCsv,
  fmtDateTime,
  fmtDistance,
  fmtQty,
} from '../format';
import {
  csvColumnLabel,
  defaultPreviewTab,
  describePickError,
  errorColumnsOf,
  errorCsvRows,
  fileGuardError,
  orderOfRow,
  ordersOfTab,
  type PickError,
  type PreviewTab,
} from './csvImportErrors';
import { useLoad } from '../hooks/useLoad';
import { buildHubClientSnapshot } from '../snapshot';
import type {
  ClientRef,
  CsvCommitResult,
  CsvDryRunResult,
  CsvImportError,
  CsvImportOrderRow,
} from '../types';
import { useWarehouse } from '../WarehouseContext';
import {
  CsvErrorTable,
  type CsvErrorItem,
  CsvFileProblem,
} from './CsvErrorTable';
import { DisabledReason } from './dialogs';
import { FormField } from './form';
import { ClientSelect, HubSelect } from './pickers';
import { codeCls, metaCls, nameCls, SumTile } from './ui';

/**
 * Upload outbound requests from CSV — mirrors the addresses/shipments import:
 * pick hub + client → file → server dry-run preview → commit. The commit is
 * all-or-nothing: the server re-validates the same file and creates nothing
 * when a single row is wrong (422 CSV_HAS_ERRORS), so the preview blocks the
 * Import button while errors exist and a failed commit swaps the fresh errors
 * back into the preview.
 */

export const CSV_COLUMNS = [
  'external_id',
  'recipient_name',
  'recipient_phone',
  'destination_address',
  'latitude',
  'longitude',
  'destination_city',
  'destination_notes',
  'expected_delivery_date',
  'service_type',
  'sku',
  'qty',
  'notes',
];
const CSV_REQUIRED = CSV_COLUMNS.filter(
  (c) =>
    !['destination_city', 'destination_notes', 'service_type', 'notes'].includes(
      c
    )
);
const MAX_BYTES = 2 * 1024 * 1024;
const MAX_ROWS = 1000;

type Stage = 'pick' | 'preview';

interface Props {
  open: boolean;
  onClose: () => void;
  onImported: (result: CsvCommitResult) => void;
  initialHubID?: number | null;
  initialClient?: ClientRef | null;
}

const ORDER_BADGE: Record<
  CsvImportOrderRow['status'],
  { label: string; status: 'success' | 'information' | 'error' }
> = {
  NEW: { label: 'Baru', status: 'success' },
  EXISTS: { label: 'Sudah ada', status: 'information' },
  ERROR: { label: 'Error', status: 'error' },
};

/** The orders table; an ERROR row names the columns that are wrong. */
const orderColumns = (
  errors: readonly CsvImportError[]
): TableDataColumn<CsvImportOrderRow>[] => [
  {
    key: 'externalID',
    title: 'External ID',
    render: (_v, o) => <span className={codeCls}>{o.externalID}</span>,
  },
  {
    key: 'recipient',
    title: 'Penerima',
    render: (_v, o) => (
      <div>
        <p className={nameCls}>{o.recipientName || '—'}</p>
        <p className={`${metaCls} max-w-[220px] truncate`}>
          {o.destinationAddress}
        </p>
      </div>
    ),
  },
  {
    key: 'rows',
    title: 'Baris CSV',
    render: (_v, o) => (
      <span className="font-mono text-[11px] text-text-sub-600">
        {o.rows.join(', ')}
      </span>
    ),
  },
  {
    key: 'distance',
    title: 'Jarak dari hub',
    align: 'right',
    // Empty by default (DISTANCE_WMS_CALCULATION_MODE=NONE): Dispatch
    // measures it; "Ubah" may set one by hand per order.
    render: (_v, o) => (
      <span
        className="text-[13px] tabular-nums text-text-sub-600"
        title={o.distance == null ? 'Dihitung saat dispatch' : undefined}
      >
        {fmtDistance(o.distance)}
      </span>
    ),
  },
  {
    key: 'lines',
    title: 'Lines / qty',
    align: 'right',
    render: (_v, o) => (
      <span className="text-[13px] tabular-nums text-text-sub-600">
        {o.lineCount} / {fmtQty(o.totalQty)}
      </span>
    ),
  },
  {
    key: 'orderStatus',
    title: 'Status',
    render: (_v, o) => {
      const b = ORDER_BADGE[o.status] ?? ORDER_BADGE.ERROR;
      return (
        <div>
          <Badge status={b.status} type="dot" size="sm">
            {o.status === 'ERROR' ? `${o.errorCount} error` : b.label}
          </Badge>
          {o.existingCode && (
            <p className="mt-0.5 font-mono text-[10px] text-text-soft-400">
              {o.existingCode}
            </p>
          )}
          {o.status === 'ERROR' && (
            <p
              className="mt-0.5 max-w-[220px] font-mono text-[10px] leading-snug text-(--state-error-dark)"
              data-testid={`csv-order-error-fields-${o.externalID}`}
            >
              {errorColumnsOf(errors, o).join(', ')}
            </p>
          )}
        </div>
      );
    },
  },
];

/**
 * Every row error, each filed under the order it belongs to (external_id +
 * recipient) in the shared error table. `only` narrows it to one order
 * (clicked in the orders table).
 */
const ErrorTable: React.FC<{
  errors: CsvImportError[];
  orders: CsvImportOrderRow[];
  only: string | null;
  onClearOnly: () => void;
  onDownload: () => void;
}> = ({ errors, orders, only, onClearOnly, onDownload }) => {
  const items: CsvErrorItem[] = errors.map((e) => {
    const order = orderOfRow(orders, e.row);
    return order
      ? {
          ...e,
          group: { code: order.externalID, sub: order.recipientName },
        }
      : e;
  });
  const shown = only ? items.filter((e) => e.group?.code === only) : items;
  return (
    <CsvErrorTable
      errors={shown}
      columnLabel={csvColumnLabel}
      groupLabel="Order"
      onDownload={onDownload}
      narrowed={
        only
          ? { label: only, total: errors.length, onClear: onClearOnly }
          : null
      }
    />
  );
};

const OutboundCsvImportModal: React.FC<Props> = ({
  open,
  onClose,
  onImported,
  initialHubID = null,
  initialClient = null,
}) => {
  const { hubs } = useWarehouse();
  const [stage, setStage] = useState<Stage>('pick');
  const [hubID, setHubID] = useState<number | null>(initialHubID);
  const [client, setClient] = useState<ClientRef | null>(initialClient);
  const [file, setFile] = useState<File | null>(null);
  const [preview, setPreview] = useState<CsvDryRunResult | null>(null);
  const [busy, setBusy] = useState<'preview' | 'commit' | 'template' | null>(
    null
  );
  const [pickError, setPickError] = useState<PickError | null>(null);
  const [commitError, setCommitError] = useState<string | null>(null);
  const [tab, setTab] = useState<PreviewTab>('ALL');
  /** Error tab narrowed to one order (clicked in the orders table). */
  const [errorOrder, setErrorOrder] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (!open) return;
    setStage('pick');
    setHubID(initialHubID);
    setClient(initialClient);
    setFile(null);
    setPreview(null);
    setBusy(null);
    setPickError(null);
    setCommitError(null);
    setTab('ALL');
    setErrorOrder(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // "Upload terakhir" is a hint, not a requirement: a failed load just
  // hides the section.
  const recentLoad = useLoad(
    (signal) =>
      warehouseApi.listImports(
        { hubID, clientID: client?.id ?? null, page: 1, limit: 4 },
        { signal }
      ),
    [hubID, client?.id],
    open
  );
  const recent = useMemo(() => recentLoad.data?.data ?? [], [recentLoad.data]);
  const columns = useMemo(
    () => orderColumns(preview?.errors ?? []),
    [preview?.errors]
  );

  if (!open) return null;

  const hub = hubs.find((h) => h.id === hubID);
  const ctx: CsvImportContext | null =
    hubID && client ? buildHubClientSnapshot(hubs, hubID, client) : null;

  const resetPreview = () => {
    setStage('pick');
    setPreview(null);
    setCommitError(null);
    setErrorOrder(null);
  };

  const pickFile = (f: File | null) => {
    setPickError(null);
    resetPreview();
    if (!f) {
      setFile(null);
      return;
    }
    // The server parses the file: only the guard is shared, nothing is read.
    const bad = csvFileError(f, MAX_BYTES);
    if (bad) {
      setFile(null);
      setPickError(fileGuardError(bad));
      return;
    }
    setFile(f);
  };

  const handleTemplate = async () => {
    if (!client) {
      setPickError({
        kind: 'form',
        title: 'Pilih client dulu',
        detail: 'Template memakai contoh SKU client yang dipilih.',
        fields: [],
        hint: null,
      });
      return;
    }
    setBusy('template');
    try {
      const { blob, filename } = await warehouseApi.downloadImportTemplate(
        client.id
      );
      downloadBlob(blob, filename);
      toast.info('Template di-download');
    } catch (err) {
      toast.error('Gagal mengunduh template', {
        description: parseWarehouseError(err).message,
      });
    } finally {
      setBusy(null);
    }
  };

  const handlePreview = async () => {
    if (!ctx || !file) return;
    setBusy('preview');
    setPickError(null);
    setCommitError(null);
    try {
      const res = await warehouseApi.dryRunImport(ctx, file);
      setPreview(res);
      setTab(defaultPreviewTab(res.errors.length));
      setErrorOrder(null);
      setStage('preview');
    } catch (err) {
      setPickError(describePickError(parseWarehouseError(err)));
    } finally {
      setBusy(null);
    }
  };

  const handleCommit = async () => {
    if (!ctx || !file || !preview) return;
    setBusy('commit');
    setCommitError(null);
    try {
      const res = await warehouseApi.commitImport(ctx, file);
      onImported(res);
    } catch (err) {
      const e = parseWarehouseError(err);
      if (e.code === 'CSV_HAS_ERRORS') {
        // The file changed (or the catalog/stock did) since the preview:
        // show the commit's own errors — nothing was created.
        const rowErrors = e.rowErrors;
        setPreview((prev) =>
          prev
            ? {
                ...prev,
                errors: rowErrors.length ? rowErrors : prev.errors,
                summary: {
                  ...prev.summary,
                  errors: rowErrors.length || prev.summary.errors || 1,
                },
              }
            : prev
        );
        setTab('ERROR');
        setErrorOrder(null);
        setCommitError(
          `${e.message} — import dibatalkan, tidak ada order yang dibuat.`
        );
      } else {
        setCommitError(e.message);
      }
    } finally {
      setBusy(null);
    }
  };

  const downloadErrors = () => {
    if (!preview) return;
    downloadCsv(
      `errors-${file?.name ?? 'upload.csv'}`,
      errorCsvRows(preview.errors, preview.orders)
    );
  };

  const s = preview?.summary;
  const canImport = !!s && s.errors === 0 && s.toCreate > 0 && busy === null;

  const pickBody = (
    <div className="flex flex-col gap-5">
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <FormField label="Hub" required>
          <HubSelect
            value={hubID}
            onChange={(id) => {
              setHubID(id);
              resetPreview();
            }}
          />
        </FormField>
        <FormField label="Client" required>
          <ClientSelect
            value={client}
            onChange={(c) => {
              setClient(c);
              resetPreview();
            }}
          />
        </FormField>
      </div>

      <div>
        <div className="mb-1.5 flex items-center justify-between">
          <span className="text-sm font-medium text-text-strong-950">
            File CSV <span className="text-(--state-error-base)">*</span>
          </span>
          <Button
            tone="primary"
            style="link"
            size="xs"
            className="text-xs font-semibold tracking-tight"
            onClick={handleTemplate}
            disabled={busy === 'template'}
            leftIcon={
              <i className="ri-download-2-line text-[14px]" aria-hidden />
            }
          >
            Download template
          </Button>
        </div>
        <div
          data-testid="csv-dropzone"
          onDragOver={(e) => e.preventDefault()}
          onDragEnter={(e) => e.preventDefault()}
          onDrop={(e) => {
            e.preventDefault();
            pickFile(e.dataTransfer.files?.[0] ?? null);
          }}
          className="rounded-xl border-2 border-dashed border-stroke-soft-200 bg-bg-weak-50 px-4 py-7 text-center transition-colors hover:border-stroke-strong-950"
        >
          <input
            ref={inputRef}
            type="file"
            accept=".csv,text/csv"
            className="hidden"
            data-testid="csv-file-input"
            onChange={(e) => {
              pickFile(e.target.files?.[0] ?? null);
              e.target.value = '';
            }}
          />
          {file ? (
            <div className="flex items-center justify-center gap-3 text-sm">
              <i
                className="ri-file-text-line text-[20px] text-accent"
                aria-hidden
              />
              <div className="text-left">
                <p className="font-medium text-text-strong-950">{file.name}</p>
                <p className={metaCls}>{(file.size / 1024).toFixed(1)} KB</p>
              </div>
              <Button
                tone="neutral"
                style="ghost"
                size="xs"
                onClick={() => pickFile(null)}
              >
                Hapus
              </Button>
            </div>
          ) : (
            <button
              type="button"
              onClick={() => inputRef.current?.click()}
              className="block w-full cursor-pointer text-sm text-text-sub-600"
            >
              <i
                className="ri-file-upload-line mx-auto mb-2 block text-[24px] text-icon-soft-400"
                aria-hidden
              />
              <span className="block">
                Drag &amp; drop CSV di sini, atau{' '}
                <span className="font-semibold text-accent">browse</span>
              </span>
              <span className={`mt-1 block ${metaCls}`}>
                .csv saja · maks {fmtQty(MAX_ROWS)} baris · 2 MB
              </span>
            </button>
          )}
        </div>
        <p className="mt-2 text-[11px] leading-relaxed text-text-sub-600">
          Kolom wajib:{' '}
          <span className="font-mono">{CSV_REQUIRED.join(', ')}</span>
        </p>
      </div>

      {pickError && <CsvFileProblem problem={pickError} />}

      {recent.length > 0 && (
        <div>
          <p className="label-eyebrow mb-2">
            <i className="ri-history-line mr-1" aria-hidden />
            Upload terakhir
          </p>
          <div className="overflow-hidden rounded-xl border border-stroke-soft-200">
            {recent.map((u) => (
              <div
                key={u.id}
                className="flex items-center justify-between gap-3 border-b border-stroke-soft-200 px-3 py-2 text-[12px] last:border-b-0"
              >
                <div className="min-w-0">
                  <p className="font-mono font-semibold text-text-strong-950">
                    {u.code}{' '}
                    <span className="font-sans font-normal text-text-sub-600">
                      · {u.fileName}
                    </span>
                  </p>
                  <p className={metaCls}>
                    {u.client?.name ?? `Client #${u.clientID}`} ·{' '}
                    {u.hub?.name ?? `Hub #${u.hubID}`} · {u.createdBy} ·{' '}
                    {fmtDateTime(u.createdAt)}
                  </p>
                </div>
                <span className="shrink-0 tabular-nums text-text-sub-600">
                  {u.createdCount} dibuat
                  {u.skippedCount ? ` · ${u.skippedCount} dilewati` : ''}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );

  const previewBody = preview && s && (
    <div className="flex flex-col gap-4">
      <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
        <SumTile
          label="Order baru"
          value={s.toCreate}
          tone="success"
          testId="csv-sum-new"
        />
        <SumTile
          label="Sudah ada · dilewati"
          value={s.existing}
          tone="information"
          testId="csv-sum-existing"
        />
        <SumTile
          label="Error"
          value={s.errors}
          tone={s.errors ? 'error' : 'neutral'}
          testId="csv-sum-errors"
        />
        <SumTile
          label="Peringatan stok"
          value={s.warnings}
          tone={s.warnings ? 'warning' : 'neutral'}
          testId="csv-sum-warnings"
        />
      </div>

      {commitError && (
        <Alert
          status="error"
          appearance="stroke"
          size="lg"
          title="Import gagal"
        >
          {commitError}
        </Alert>
      )}

      {preview.ignoredColumns.length > 0 && (
        <Alert
          status="warning"
          appearance="stroke"
          size="lg"
          title="Kolom tidak dikenal diabaikan"
        >
          <span className="font-mono">{preview.ignoredColumns.join(', ')}</span>
        </Alert>
      )}

      {s.errors > 0 && (
        <Alert
          status="warning"
          appearance="stroke"
          size="lg"
          title="Import all-or-nothing"
          data-testid="csv-all-or-nothing"
        >
          Perbaiki semua error di file lalu upload ulang. Tidak ada order yang
          dibuat selama masih ada error.
        </Alert>
      )}

      {s.errors === 0 && s.toCreate === 0 && (
        <Alert
          status="information"
          appearance="stroke"
          size="lg"
          title="Tidak ada order baru"
        >
          Semua external_id di file ini sudah ada.
        </Alert>
      )}

      {preview.warnings.length > 0 && (
        <Alert
          status="information"
          appearance="stroke"
          size="lg"
          title="Stok belum cukup"
        >
          {preview.warnings.map((w, i) => (
            <span key={w.sku}>
              {i > 0 && ' · '}
              <span className="font-mono">{w.sku}</span> total{' '}
              {fmtQty(w.requested)}, tersedia {fmtQty(w.available)}
            </span>
          ))}
        </Alert>
      )}

      <Tabs
        value={tab}
        onValueChange={(v) => {
          setTab(v as PreviewTab);
          if (v !== 'ERROR') setErrorOrder(null);
        }}
      >
        <TabsList
          variant="line"
          className="h-auto min-h-10 w-auto flex-wrap border-0"
          data-testid="csv-preview-tabs"
        >
          <TabsTrigger value="ALL" data-testid="csv-tab-all">
            Semua order
            <TabsCount>{preview.orders.length}</TabsCount>
          </TabsTrigger>
          <TabsTrigger value="ERROR" data-testid="csv-tab-error">
            Error
            <TabsCount>{s.errors}</TabsCount>
          </TabsTrigger>
          <TabsTrigger value="NEW" data-testid="csv-tab-new">
            Order baru
            <TabsCount>{s.toCreate}</TabsCount>
          </TabsTrigger>
          <TabsTrigger value="EXISTS" data-testid="csv-tab-exists">
            Sudah ada
            <TabsCount>{s.existing}</TabsCount>
          </TabsTrigger>
        </TabsList>
      </Tabs>

      {tab === 'ERROR' ? (
        <ErrorTable
          errors={preview.errors}
          orders={preview.orders}
          only={errorOrder}
          onClearOnly={() => setErrorOrder(null)}
          onDownload={downloadErrors}
        />
      ) : (
        <div className="max-h-[300px] overflow-auto">
          <TableData
            size="sm"
            data={ordersOfTab(preview.orders, tab)}
            columns={columns}
            onRowClick={(o) => {
              if (o.status !== 'ERROR') return;
              setErrorOrder(o.externalID);
              setTab('ERROR');
            }}
            emptyState={
              <p className={`${metaCls} px-3 py-6 text-center`}>
                Tidak ada order di tab ini.
              </p>
            }
          />
        </div>
      )}
    </div>
  );

  /** Why Preview / Import is off (visible next to the button). */
  const previewBlocked = !hubID
    ? 'Pilih hub'
    : !client
      ? 'Pilih client'
      : !file
        ? 'Pilih file CSV'
        : null;
  const importBlocked = !s
    ? null
    : s.errors > 0
      ? `${s.errors} baris error — perbaiki dulu`
      : s.toCreate === 0
        ? 'Tidak ada order baru'
        : null;

  const footer =
    stage === 'pick' ? (
      <div className="flex items-center justify-end gap-2">
        {previewBlocked && (
          <DisabledReason testId="csv-preview-reason" className="mr-auto">
            {previewBlocked}
          </DisabledReason>
        )}
        <Button
          tone="neutral"
          style="stroke"
          onClick={onClose}
          disabled={busy !== null}
        >
          Batal
        </Button>
        <Button
          onClick={handlePreview}
          disabled={!ctx || !file || busy !== null}
          loading={busy === 'preview'}
          leftIcon={<i className="ri-eye-line text-[14px]" />}
        >
          Preview
        </Button>
      </div>
    ) : (
      <div className="flex items-center gap-2">
        <Button
          tone="neutral"
          style="stroke"
          onClick={resetPreview}
          disabled={busy !== null}
          leftIcon={<i className="ri-arrow-left-line text-[14px]" />}
        >
          Ganti file
        </Button>
        <span className="flex-1" />
        {importBlocked && (
          <DisabledReason testId="csv-import-reason">
            {importBlocked}
          </DisabledReason>
        )}
        <Button
          tone="neutral"
          style="stroke"
          onClick={onClose}
          disabled={busy !== null}
        >
          Batal
        </Button>
        <Button
          onClick={handleCommit}
          disabled={!canImport}
          loading={busy === 'commit'}
          leftIcon={<i className="ri-check-line text-[14px]" />}
          data-testid="csv-import"
        >
          {s && s.errors === 0 && s.toCreate > 0
            ? `Import ${s.toCreate} order`
            : 'Import'}
        </Button>
      </div>
    );

  return (
    <Modal
      isOpen={open}
      onClose={onClose}
      icon="ri-upload-2-line"
      title={
        stage === 'pick'
          ? 'Upload outbound request (CSV)'
          : 'Preview upload CSV'
      }
      subtitle={
        stage === 'pick'
          ? undefined
          : `${file?.name ?? ''} · ${client?.name ?? ''} · ${hub?.name ?? ''} · ${s?.rows ?? 0} baris · ${s?.orders ?? 0} order`
      }
      width="full"
      className={stage === 'pick' ? 'max-w-3xl' : 'max-w-4xl'}
      closeOnBackdropClick={busy === null}
      closeOnEscape={busy === null}
      footer={footer}
    >
      {stage === 'pick' ? pickBody : previewBody}
    </Modal>
  );
};

export default OutboundCsvImportModal;
